/**
 * Composant affichant les référents sollicités avec leurs vérifications de sécurité
 * Fusion de ReferentsTable et ReliabilityScore
 */

interface ReferentCheck {
  title: string;
  description: string;
  isValid: boolean;
  status: 'conforme' | 'attention' | 'non-conforme' | 'non-communique';
}

interface Referent {
  id: string;
  name: string;
  position?: string;
  company?: string;
  email: string;
  status: string;
  checks: {
    ipAddress: ReferentCheck;
    professionalEmail: ReferentCheck;
    charter: ReferentCheck;
    device: ReferentCheck;
    relationshipLevel: ReferentCheck;
    dates: ReferentCheck;
  };
}

interface ReferentDetailedCardProps {
  referents: Referent[];
}

export const ReferentDetailedCard = ({ referents }: ReferentDetailedCardProps) => {
  // Fonction pour obtenir le badge de statut
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'Complété': { 
        bg: 'bg-green-500/10',
        color: 'text-green-500'
      },
      'A répondu': { 
        bg: 'bg-blue-500/10',
        color: 'text-blue-500'
      },
      'Invité': { 
        bg: 'bg-amber-500/10',
        color: 'text-amber-500'
      },
      'Relancé': { 
        bg: 'bg-orange-500/10',
        color: 'text-orange-500'
      },
      'Refusé': { 
        bg: 'bg-rose-500/10',
        color: 'text-rose-500'
      }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig['Invité'];

    return (
      <div className={`px-2.5 py-1 ${config.bg} rounded-full flex justify-center items-center gap-2.5`}>
        <div className="justify-start">
          <span className={`${config.color} text-xs font-medium font-['Bricolage_Grotesque'] capitalize tracking-tight`}>
            {status.charAt(0)}
          </span>
          <span className={`${config.color} text-xs font-medium font-['Bricolage_Grotesque'] lowercase tracking-tight`}>
            {status.slice(1).toUpperCase()}
          </span>
        </div>
      </div>
    );
  };

  // Fonction pour obtenir l'icône selon le statut
  const getCheckIcon = (status: 'conforme' | 'attention' | 'non-conforme' | 'non-communique') => {
    if (status === 'conforme') {
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3.70033 10.0633C3.53815 10.0097 3.37041 9.97472 3.20033 9.95909L3.08283 9.95409C2.30116 9.95409 1.66699 10.6066 1.66699 11.4108C1.66699 12.1391 2.18616 12.7424 2.86449 12.8508C2.88783 12.8619 2.92588 12.886 2.97866 12.9233C3.20366 13.0858 3.69533 13.5374 4.33116 14.6824C4.57949 15.1308 5.04033 15.4099 5.54199 15.4158C5.88194 15.4197 6.21157 15.299 6.46866 15.0766M12.5003 4.58325C11.3753 5.01242 10.3153 5.82409 9.39783 6.69159C9.07783 6.99409 8.76449 7.31325 8.46199 7.64075M18.2478 5.52492C18.5145 6.27325 18.1395 6.78575 17.4162 7.28992C16.8312 7.69659 16.087 8.13742 15.2978 8.89159C14.5245 9.63075 13.7695 10.5208 13.0987 11.3966C12.278 12.4573 11.5238 13.5677 10.8403 14.7216C10.7194 14.9341 10.544 15.1105 10.3322 15.2327C10.1204 15.3549 9.87983 15.4183 9.63533 15.4166C9.3906 15.4113 9.15165 15.3412 8.94288 15.2134C8.73411 15.0856 8.56299 14.9046 8.44699 14.6891C7.82366 13.5533 7.34199 13.1049 7.12033 12.9441C6.50616 12.4966 5.83366 12.4191 5.83366 11.4441C5.83366 10.6466 6.45533 9.99992 7.22283 9.99992C7.77116 10.0224 8.27449 10.2574 8.71366 10.5774C8.99866 10.7849 9.30116 11.0591 9.61533 11.4208C9.98366 10.8758 10.4278 10.2474 10.9262 9.59659C11.6495 8.65242 12.5028 7.63825 13.4178 6.76409C14.3178 5.90409 15.3595 5.09909 16.462 4.67409C17.1812 4.39659 17.9803 4.77659 18.2478 5.52492Z" stroke="#32CD32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    } else if (status === 'attention') {
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 7.5V11.6667" stroke="#FFC506" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9.99986 17.8416H4.94986C2.0582 17.8416 0.849863 15.775 2.24986 13.25L4.84986 8.56665L7.29986 4.16665C8.7832 1.49165 11.2165 1.49165 12.6999 4.16665L15.1499 8.57498L17.7499 13.2583C19.1499 15.7833 17.9332 17.85 15.0499 17.85H9.99986V17.8416Z" stroke="#FFC506" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9.99512 14.1667H10.0026" stroke="#FFC506" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    } else if (status === 'non-communique') {
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="7" stroke="#9CA3AF" strokeWidth="1.5" fill="none"/>
          <line x1="6" y1="10" x2="14" y2="10" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    } else {
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14.0625 5.9375L5.9375 14.0625M5.9375 5.9375L14.0625 14.0625" stroke="#FF515D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
  };

  // Fonction pour obtenir les styles selon le statut
  const getCheckStyles = (status: 'conforme' | 'attention' | 'non-conforme' | 'non-communique') => {
    if (status === 'conforme') {
      return {
        iconBg: 'bg-emerald-50',
        badgeBg: 'bg-emerald-50',
        badgeColor: 'text-green-500',
        label: 'Conforme'
      };
    } else if (status === 'attention') {
      return {
        iconBg: 'bg-yellow-50',
        badgeBg: 'bg-yellow-50',
        badgeColor: 'text-yellow-400',
        label: 'ATTENTION'
      };
    } else if (status === 'non-communique') {
      return {
        iconBg: 'bg-gray-100',
        badgeBg: 'bg-gray-100',
        badgeColor: 'text-gray-400',
        label: 'Non communiqué'
      };
    } else {
      return {
        iconBg: 'bg-rose-50',
        badgeBg: 'bg-rose-50',
        badgeColor: 'text-rose-500',
        label: 'Non Conforme'
      };
    }
  };

  // Fonction pour rendre une carte de vérification
  const renderCheckCard = (check: ReferentCheck, isRefused: boolean) => {
    const styles = getCheckStyles(check.status);
    
    return (
      <div className={`flex-1 p-3 bg-gray-50 rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex justify-between items-center ${isRefused ? 'opacity-50 grayscale' : ''}`}>
        <div className="h-9 flex justify-start items-start gap-2">
          <div className="flex justify-start items-center gap-2">
            <div className={`size-9 p-2 ${styles.iconBg} rounded-lg inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden`}>
              {getCheckIcon(check.status)}
            </div>
            <div className="inline-flex flex-col justify-start items-start gap-px">
              <div className="justify-start text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
                {check.title}
              </div>
              <div className="justify-start text-neutral-400 text-[10px] font-normal font-['Bricolage_Grotesque'] capitalize">
                {check.description}
              </div>
            </div>
          </div>
        </div>
        <div className={`px-1.5 py-1 ${styles.badgeBg} rounded-full flex justify-center items-center gap-2.5`}>
          <div className={`justify-start ${styles.badgeColor} text-[10px] font-medium font-['Bricolage_Grotesque'] capitalize tracking-tight`}>
            {styles.label}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex flex-col justify-start items-start overflow-hidden">
      {/* Header Section */}
      <div className="w-full p-4 bg-gray-50 border-b border-neutral-400/20 flex justify-start items-center gap-2.5">
        <div className="justify-start text-gray-800 text-xl font-medium font-['Bricolage_Grotesque']">
          Référents sollicités ({referents.length})
        </div>
      </div>

      {/* Referent Cards */}
      {referents.map((referent, index) => {
        const isRefused = referent.status === 'Refusé';
        
        return (
          <div 
            key={referent.id} 
            className={`self-stretch p-4 flex flex-col justify-start items-start gap-6 ${index !== referents.length - 1 ? 'border-b border-neutral-400/20' : ''}`}
          >
            {/* Reference Header */}
            <div className="self-stretch inline-flex justify-between items-center">
              <div className="inline-flex flex-col justify-center items-start gap-0.5">
                <div className="justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque'] capitalize tracking-tight">
                  {referent.name}
                </div>
                <div className="w-32 justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
                  {referent.position || 'Position non spécifiée'}
                </div>
                <div className="w-32 justify-start text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
                  {referent.company || '-'}
                </div>
              </div>
              <div className="p-2.5 flex justify-center items-center gap-2.5">
                <div className="justify-start text-gray-800 text-sm font-normal font-['Bricolage_Grotesque'] lowercase tracking-tight">
                  {referent.email}
                </div>
              </div>
              {getStatusBadge(referent.status)}
            </div>

            {/* Verification Grid */}
            <div className="self-stretch flex flex-col justify-start items-start gap-3">
              {/* Row 1 */}
              <div className="self-stretch inline-flex justify-start items-center gap-3">
                {renderCheckCard(referent.checks.ipAddress, isRefused)}
                {renderCheckCard(referent.checks.professionalEmail, isRefused)}
              </div>

              {/* Row 2 */}
              <div className="self-stretch inline-flex justify-start items-center gap-3">
                {renderCheckCard(referent.checks.charter, isRefused)}
                {renderCheckCard(referent.checks.device, isRefused)}
              </div>

              {/* Row 3 */}
              <div className="self-stretch inline-flex justify-start items-center gap-3">
                {renderCheckCard(referent.checks.relationshipLevel, isRefused)}
                {renderCheckCard(referent.checks.dates, isRefused)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
